<template>
  <ExpandCard
    style="
      --max-height: var(--vh-13);
      --min-height: 3.25rem;
      --clr-back-content: var(--clr-black-1-3);
      --content-padding: 0 0.5rem;
      --radius-open: 4px;
      --radius-closed: 4px;
      --transition: 0s;
      --transition-content: 0s;
      --icon-opener-margin: 0 0 0 0;
    "
    :opened="expanded"
    :hasScrollIntoView="false"
    @click.stop
    @update:open="
      open => {
        if (expanded === open) return
        $emit('update:open')
      }
    "
  >
    <template #title>
      <HaTitle
        :correctness_group="correctness_group"
        :hand="hand"
        :board="board"
      />
      <!-- expand icon in Hints is slightly smaller so this 0.45gap is to balance that -->
      <div
        class="d-flex"
        style="margin: 0 0.5rem 0 auto; gap: 0.45rem"
      >
        <div class="csst csst_nm f-center">
          <router-link
            class="icon_btn clr-grey"
            style="padding: 0.325rem"
            target="_blank"
            :to="
              links_getLink({
                pathName: 'StudySolutions',
                asRouterLink: true,
                customQuery: lastSpotLinkQuery,
              })
            "
          >
            <i class="mdi mdi-school tracrls_icon"></i>
          </router-link>
          <div
            class="csst_content tracrls_btn_tooltip text-nowrap clr-white text-medium"
            style="position: absolute; left: -0.25rem; transform: translateX(-100%)"
            v-html="$t('tooltips.study_spot')"
          ></div>
        </div>
        <div
          style="padding: 0.325rem"
          class="csst csst_nm icon_btn mdi mdi-star text-normal-large"
          :style="item.marked ? 'color:var(--clr-yellow)' : 'color: var(--clr-grey-2);'"
          :class="item.marked ? 'mdi-star' : 'mdi-star-outline'"
          @click.stop="toggleMarked()"
        >
          <div
            class="csst_content text-normal clr-white"
            style="position: absolute; left: -0.25rem; transform: translateX(-100%)"
          >
            <span>{{ $t(`common.${item.marked ? 'starred' : 'not_starred'}`) }} </span>
          </div>
        </div>
      </div>
    </template>

    <template #default="{ open }">
      <div
        v-if="open"
        class="ha-street-blocks"
        @click.stop
      >
        <div class="ha-street-block">
          <HaControls
            @click:replay="$emit('click:replay', l_item)"
            @click:capture="capt_capture()"
          />
        </div>
        <div
          id="capture"
          class="f-column"
          style="background: var(--clr-back-content); gap: var(--ha-gap)"
        >
          <HandDetailLogo
            v-if="capt_inProgress"
            style="position: absolute; top: 6.25rem; right: 1rem"
          />

          <div
            class="f-column"
            style="gap: var(--ha-gap)"
          >
            <div class="ha-street-block">
              <HaGameformat :item="analyzeItem.haGmfItem" />
              <HaPlayers
                :players="analyzeItem.players"
                :hero="analyzeItem.hero.position"
                :opponent="analyzeItem.opponent.position"
              />
            </div>
            <AnalyzeStreetBlocks
              :item="analyzeItem"
              :showBetsIn="showBetsIn"
              :showEvIn="showEvIn"
              :replayedSpotId="replayedSpotId"
            />
          </div>
        </div>
      </div>
    </template>
  </ExpandCard>
</template>

<script>
import ExpandCard from '@/components/Common/ExpandCard/ExpandCard'
import { getGameformat } from '@/store/gameformat/gameformat'
import { getCardsTillStreet } from '@/helpers/cards'
import {
  getCompleteHistorySpots,
  getGroupFoldHistorySpots,
  setHistoryEventType,
  getHistorySpotsUrlParams,
  getLastSpotsOpponent,
} from '@/store/gamestate/gamestate'
import HaPlayers from '@/components/AnalyzeStreetBlocks/HaPlayers'
import HaGameformat from '@/components/AnalyzeStreetBlocks/HaGameformat'
import HaTitle from '@/components/AnalyzeStreetBlocks/HaTitle'
import HaControls from '@/components/AnalyzeStreetBlocks/HaControls'
import AnalyzeStreetBlocks from '@/components/AnalyzeStreetBlocks/AnalyzeStreetBlocks'

import capture_mix from '@/mixins/capture_mix'
import format_mix from '@/mixins/format_mix'
import links_mix from '@/mixins/links_mix'
import float_mix from '@/mixins/float_mix'
import HandDetailLogo from '@/components/hhv3/Analyzer/HandDetail/HandDetailLogo.vue'

export default {
  name: 'TrainerHistoryItem',
  components: {
    HandDetailLogo,
    AnalyzeStreetBlocks,
    HaPlayers,
    HaGameformat,
    HaTitle,
    HaControls,
    ExpandCard,
  },
  mixins: [format_mix, links_mix, capture_mix, float_mix],
  props: {
    item: Object,
    expanded: Boolean,
    showBetsIn: String,
    showEvIn: String,
    replayedSpotId: Number,
  },
  emits: ['update:open', 'click:replay'],
  methods: {
    toggleMarked() {
      const marked = !this.item.marked
      this.$store.dispatch('history_update_hand', {
        hand: {
          ...this.item,
          marked,
        },
      })
      const endpoint = `poker/practice/practice-hands/${this.l_item.trainer_state.trainer_practice_hand_id}/`
      this.$store.dispatch('api_call', {
        method: 'PATCH',
        hasCancel: false,
        endpoint,
        data: { marked },
        message: {
          type: 'error',
        },
      })
    },
  },
  computed: {
    l_item() {
      return JSON.parse(this.item.stringified_hand)
    },
    l_open() {
      return this.open && this.expanded
    },
    analyzeItem() {
      const { gameformats } = this.$store.getters

      const { query_trainer_history } = this.l_item
      const gameformat = getGameformat({ item: query_trainer_history, gameformats })

      const board = query_trainer_history.board_tmp
      const hero_position = query_trainer_history.fh_hero_tmp
      const boards = [board]

      let { history_spots } = this.l_item
      history_spots = history_spots.map(hs => {
        const selected_action = hs.available_actions.find(a => a.is_selected)

        const r = {
          ...hs,
          selected_action,
        }
        return r
      })
      // console.log(history_spots)
      // debugger
      history_spots = getHistorySpotsUrlParams({ history_spots, board })
      history_spots = setHistoryEventType({ history_spots })

      const hand_hero = query_trainer_history.hand_tmp
      const hand_opponent = query_trainer_history.hand_op_tmp
      const lastSpot = history_spots[history_spots.length - 1]
      let opponent = getLastSpotsOpponent({
        history_spots,
        gameformat,
      })

      const { players } = lastSpot
      opponent = {
        position: opponent?.position,
        hand: hand_opponent,
      }

      const hero = {
        position: hero_position,
        hand: hand_hero,
      }

      history_spots = getCompleteHistorySpots({
        history_spots,
        board,
        addMissingBoardSpots: true,
        hasFirstBoardSpot: true,
      })
      history_spots = getGroupFoldHistorySpots({
        history_spots,
        hero_position,
        onlyRealFolds: false,
      })
      history_spots = history_spots.map(i => ({ ...i, is_hero: !i.is_bot }))

      // remove hanging next-street-board spot X-X (in custom-spot mode)
      const completeLastSpot = history_spots[history_spots.length - 1]
      // console.log(history_spots)
      if (
        lastSpot &&
        completeLastSpot.type === 'board' &&
        completeLastSpot.street !== lastSpot.street
      ) {
        history_spots.pop()
      }

      // console.log(this.l_item)
      // debugger
      const gameProperties = this.l_item?.trainer_state?.trainer_api_game_properties
      const custom_tree = this.l_item?.trainer_state?.trainer_custom_tree
      const haGmfItem = {
        custom_tree,
        variant: gameProperties?.variant || '',
        format: gameProperties?.format || '',
        players,
        // Date
      }

      const res = {
        gameformat,
        history_spots,
        lastSpot,
        players,
        boards,
        hero,
        opponent: opponent?.position ? opponent : {},
        haGmfItem,
      }
      // console.log(res)
      return res
    },
    lastSpotLinkQuery() {
      const lastSpot = this.analyzeItem?.lastSpot
      const gmf = this.analyzeItem?.gameformat
      const res = {
        gametype: gmf?.gametype,
        depth: gmf?.depth,
        stacks: gmf?.stacks,
        ...lastSpot?.urlParams,
        soltab: 'strategy',
        cussol_id: lastSpot?.custom_solution_id || '',
        solution_type: lastSpot?.custom_solution_id ? 'custom' : 'gwiz',
      }
      return res
    },
    query() {
      return this.l_item?.query_trainer_history
    },
    spot() {
      return this.l_item?.history_spot
    },
    correctness() {
      const res = this.l_item?.correctness || {}
      return res
    },
    correctness_group() {
      return this.correctness?.correctness_group || {}
    },
    hand() {
      const res = this.query.hand_tmp || ''
      return res
    },
    street() {
      return this.l_item?.street
    },
    board() {
      // console.log('hello')
      const board = this.query.board_tmp || ''
      const res = getCardsTillStreet(board, this.street)
      return res
    },
  },
}
</script>
